// Classifies a request failure caused by the transport (navigation abort, offline, no response), never
// an HTTP error response, and decides when a no-response GET is retried. A GET still failing after its
// retry stays reportable online, as it can mean a misconfiguration or an unreachable service.
import { AxiosError, isAxiosError, isCancel } from 'axios'

export enum TransportFailure {
  NavigationAbort = 'navigation_abort',
  Offline = 'offline',
  NoResponse = 'no_response'
}

const BROWSER_ABORT_MESSAGE = 'Request aborted'
const NO_RESPONSE_CODES = new Set<string>([AxiosError.ERR_NETWORK, AxiosError.ETIMEDOUT, AxiosError.ECONNABORTED])

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError'

const isNavigationAbort = (error: unknown): boolean =>
  isCancel(error) ||
  isAbortError(error) ||
  (isAxiosError(error) && error.code === AxiosError.ECONNABORTED && error.message === BROWSER_ABORT_MESSAGE)

const isNoResponse = (error: unknown): boolean =>
  isAxiosError(error) && !error.response && NO_RESPONSE_CODES.has(error.code ?? '')

/**
 * Names the transport failure behind `error`, or undefined when it is not one. `online` is the
 * browser's `navigator.onLine` at the time of the failure.
 */
export const transportFailureOf = (error: unknown, online: boolean): TransportFailure | undefined => {
  if (isNavigationAbort(error)) return TransportFailure.NavigationAbort
  if (!isNoResponse(error)) return undefined
  return online ? TransportFailure.NoResponse : TransportFailure.Offline
}

export const isExpectedTransportFailure = (error: unknown, online: boolean): boolean => {
  const failure = transportFailureOf(error, online)
  return failure === TransportFailure.NavigationAbort || failure === TransportFailure.Offline
}

export const MAX_NO_RESPONSE_RETRIES = 1
export const NO_RESPONSE_RETRY_DELAY_MS = 500

/** True when a GET that got no response, and was not cancelled, is retried after `attempt` earlier retries. */
export const shouldRetryNoResponse = (error: unknown, attempt: number): boolean =>
  attempt < MAX_NO_RESPONSE_RETRIES &&
  !isNavigationAbort(error) &&
  isNoResponse(error) &&
  isAxiosError(error) &&
  error.config?.method?.toLowerCase() === 'get'

export interface FailedRequestContext {
  readonly request_url: string | undefined
  readonly navigator_online: boolean
  readonly transport_failure: TransportFailure | undefined
}

export const failedRequestContext = (error: unknown, online: boolean): FailedRequestContext => ({
  request_url: isAxiosError(error) ? error.config?.url : undefined,
  navigator_online: online,
  transport_failure: transportFailureOf(error, online)
})
